import React, { useState, useCallback } from 'react';
import { Modal, Span, Input, Gap } from '@salecycle/ui-component-library';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useParams } from 'react-router-dom';
import useNotifications from 'core/notifications/useNotifications';
import { importSnapshot } from 'sections/design/template-management/sections/template-editor/utils/queries';
import { isValidSnapshotId } from '../../utils/validateSnapshotId';

export interface RcsImportSnapshotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (snapshot: { compilationContext?: unknown }) => void;
}

export const RcsImportSnapshotModal: React.FC<RcsImportSnapshotModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useTranslation(['designs', 'trackingLinks']);
  const { siteId } = useParams<{ siteId?: string }>();
  const { showNotification } = useNotifications();
  const [snapshotId, setSnapshotId] = useState('');
  const [validationMessage, setValidationMessage] = useState<string>('');

  const handleClose = useCallback(() => {
    setSnapshotId('');
    setValidationMessage('');
    onClose();
  }, [onClose]);

  const importMutation = useMutation((id: string) => importSnapshot({ siteId: siteId!, snapshotId: id, t }), {
    onSuccess: data => {
      onSuccess(data);
      handleClose();
    },
    onError: () => {
      showNotification(t('designs:rcsTemplateEditor.snapshotTab.importModal.notificationError'), 'error');
    },
  });

  const handleConfirm = useCallback(() => {
    const trimmedSnapshotId = snapshotId.trim();
    if (!isValidSnapshotId(trimmedSnapshotId)) {
      setValidationMessage(t('designs:rcsTemplateEditor.snapshotTab.importModal.validationInvalidId'));
      return;
    }
    setValidationMessage('');
    importMutation.mutate(trimmedSnapshotId);
  }, [snapshotId, importMutation, t]);

  const handleSnapshotIdChange = useCallback((value: string) => {
    setSnapshotId(value.trim());
    setValidationMessage('');
  }, []);

  const isConfirmDisabled = snapshotId.trim().length === 0 || !!validationMessage || importMutation.isLoading;

  if (!siteId) {
    return null;
  }

  return (
    <div data-testid="RcsImportSnapshotModal">
      <Modal
        heading={t('designs:rcsTemplateEditor.snapshotTab.importModal.heading')}
        variation="clean"
        isOpen={isOpen}
        onCloseButtonClicked={handleClose}
        onCancelButtonClicked={handleClose}
        onOkButtonClicked={handleConfirm}
        okButtonLabel={t('designs:rcsTemplateEditor.snapshotTab.importModal.confirm')}
        cancelButtonLabel={t('designs:rcsTemplateEditor.snapshotTab.importModal.cancel')}
        okButtonDisabled={isConfirmDisabled}
        okButtonType="primary"
      >
        <Span>{t('designs:rcsTemplateEditor.snapshotTab.importModal.description')}</Span>
        <Gap top={20} />
        <Input
          ariaLabel={t('designs:rcsTemplateEditor.snapshotTab.importModal.snapshotIdLabel')}
          label={t('designs:rcsTemplateEditor.snapshotTab.importModal.snapshotIdLabel')}
          placeholder={t('designs:rcsTemplateEditor.snapshotTab.importModal.snapshotIdLabel')}
          value={snapshotId}
          onChange={handleSnapshotIdChange}
          tooltipHeading={t('designs:rcsTemplateEditor.snapshotTab.importModal.snapshotIdTooltipHeading')}
          tooltipDescription={t('designs:rcsTemplateEditor.snapshotTab.importModal.snapshotIdTooltipDescription')}
          validationMessage={validationMessage}
          data-testid="RcsImportSnapshotModalSnapshotIdInput"
        />
      </Modal>
    </div>
  );
};
